import { Box, Chip, Tooltip } from '@mui/material'
import { z } from 'zod'
import { WhiteTooltip } from './prepare'

export const getColumns = (sources = [], dataTypes = []) => [
  {
    id: 'loadId',
    label: 'Load id',
    width: '120px',
    align: 'left',
  },
  {
    id: 'transformationType',
    label: 'Source',
    width: '150px',
    type: 'dropdown',
    align: 'left',
    options: sources.map(src => ({ label: src.value, value: src.value })),
    // When the file name is blank, do not show the Source column value
    render: (value, row) => (row?.fileName ? value : ''),
  },
  {
    id: 'fileDataType',
    label: 'Data type',
    width: '180px',
    type: 'dropdown',
    align: 'left',
    options: dataTypes.map(type => ({ label: type, value: type })),
  },
  {
    id: 'fileName',
    label: 'File name',
    width: '200px',
    align: 'left',
    render: value => {
      // If value is an array of file names (Alt source with multiple files)
      if (Array.isArray(value)) {
        if (value.length === 1) {
          return (
            <Tooltip title={value[0]} arrow placement="top">
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {value[0]}
              </span>
            </Tooltip>
          )
        }
        return (
          <Tooltip
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {value.map((name, idx) => (
                  <span key={idx}>{name}</span>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {value[0]}
              </span>
              <Chip
                label={`+${value.length - 1}`}
                size="small"
                sx={{
                  fontSize: '11px',
                  height: '20px',
                  backgroundColor: '#e0e0e0',
                }}
              />
            </Box>
          </Tooltip>
        )
      }
      // Single file name (string)
      return (
        <Tooltip title={value || ''} arrow placement="top">
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {value || ''}
          </span>
        </Tooltip>
      )
    },
  },
  {
    id: 'fileStatus',
    label: 'Status',
    width: '150px',
    align: 'center',
    render: (value, row) => {
      const getStatusColor = status => {
        switch (status?.toUpperCase()) {
          case 'UPLOADED':
            return { backgroundColor: '#F0FDF3', color: '#197C37' }
          case 'FAILED':
          case 'DELETED':
            return { backgroundColor: '#FEF3F2', color: '#E0301E' }
          case 'IN PROGRESS':
            return { backgroundColor: '#FFDFC4', color: '#D17300' }
          case 'PARTIALLY UPLOADED':
            return { backgroundColor: '#FFDFC4', color: '#D17300' }
          default:
            return { backgroundColor: '#E2E3E5', color: '#495057' }
        }
      }

      const { backgroundColor, color } = getStatusColor(value)

      const badge = (
        <span
          style={{
            backgroundColor,
            color,
            padding: '2px 6px',
            borderRadius: 4,
            display: 'inline-block',
            cursor: 'pointer',
          }}
        >
          {value || 'N/A'}
        </span>
      )

      if (value?.toUpperCase() === 'FAILED') {
        return (
          <WhiteTooltip placement="top" title={row?.errorMessage || ''} arrow>
            <span>{badge}</span>
          </WhiteTooltip>
        )
      } else {
        return (
          <WhiteTooltip placement="top" title={row?.statusMessage || ''} arrow>
            <span>{badge}</span>
          </WhiteTooltip>
        )
      }

      return badge
    },
  },

  {
    id: 'dataCount',
    label: 'Data count',
    width: '150px',
    align: 'center',
  },
  {
    id: 'lastModifiedBy',
    label: 'Uploaded by',
    width: '150px',
  },
  {
    id: 'lastModifiedDate',
    label: 'Uploaded date',
    width: '150px',
  },
]

const ddMMYYYYWithSlash = z
  .string()
  .regex(
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
    'Invalid format. Example: 21/10/2024 (dd/mm/yyyy)'
  )

export const getSearchFields = (sources = [], dataTypes = []) => [
  ...getColumns(sources, dataTypes).map(col => {
    if (col.id === 'lastModifiedDate') {
      return {
        ...col,
        type: 'text',
        placeholder: `Example: 24/11/20219 (dd/mm/yyyy)`,
        zod: ddMMYYYYWithSlash,
      }
    }
    return {
      ...col,
      type: col.type || 'text',
      placeholder: `Enter ${col.label}`,
    }
  }),
]
